/** How a case's outcome and score are named, banded and coloured. */

/**
 * The states a case can be in for a given run, aggregated over its iterations:
 * `mixed` passed some and not others, `errored` never produced a judgement, and
 * `not_run` was outside the run's tag selector or has not landed yet.
 */
export const CaseOutcome = {
  PASSED: "passed",
  MIXED: "mixed",
  FAILED: "failed",
  ERRORED: "errored",
  NOT_RUN: "not_run",
} as const;

export type CaseOutcome = (typeof CaseOutcome)[keyof typeof CaseOutcome];

/**
 * Per-outcome treatment for the glyph and badge channels, carrying no cell fill:
 * the heatmap is coloured by score (`SCORE_BAND_CONFIG`) and outcome rides an
 * independent shape channel on top, so a case that scores well and still fails an
 * assertion can say both.
 *
 * `glyph` names a shape, which is the accessibility channel for the red/green
 * fills — never drop them to "clean up" the grid. `passed` has none on purpose:
 * an unmarked cell is the calm default.
 */
export const CASE_OUTCOME_CONFIG = {
  [CaseOutcome.PASSED]: {
    label: "Passed",
    badgeClass: "border-green-300 bg-green-100 text-green-700",
    glyph: null,
  },
  [CaseOutcome.MIXED]: {
    label: "Mixed",
    badgeClass: "border-orange-300 bg-orange-100 text-orange-700",
    glyph: "mixed",
  },
  [CaseOutcome.FAILED]: {
    label: "Failed",
    badgeClass: "border-red-300 bg-red-100 text-red-700",
    glyph: "failed",
  },
  [CaseOutcome.ERRORED]: {
    label: "Errored",
    badgeClass: "border-red-300 bg-red-50 text-red-700",
    glyph: "errored",
  },
  [CaseOutcome.NOT_RUN]: {
    label: "Not run",
    badgeClass: "border-gray-300 bg-gray-100 text-gray-600",
    glyph: null,
  },
} as const satisfies Record<
  CaseOutcome,
  {
    label: string;
    badgeClass: string;
    glyph: "mixed" | "failed" | "errored" | null;
  }
>;

/**
 * The four levels of the judge rubric, which is also the heatmap's colour scale.
 * Not invented buckets: the judges score 1-4 against instructions naming each
 * integer, so a band is the rubric level a mean score rounds to.
 */
export const ScoreBand = {
  CORRECT: "correct",
  MOSTLY_CORRECT: "mostly_correct",
  MOSTLY_INCORRECT: "mostly_incorrect",
  INCORRECT: "incorrect",
} as const;

export type ScoreBand = (typeof ScoreBand)[keyof typeof ScoreBand];

/**
 * The score ramp: diverging in hue at the rubric's correct/incorrect split, and
 * monotone in lightness from `correct` to `incorrect` so it stays legible with no
 * colour at all. A symmetric diverging scale puts a pale green beside a pale red,
 * which measured ΔE 7.1 under deuteranopia.
 *
 * Each mode's steps are validated against that mode's own card, never flipped
 * (OKLab x100, Machado 2009 at severity 1.0, all pairs — any two cells can end up
 * adjacent): light CVD ΔE 10.9, dark CVD ΔE 11.9, against ΔE 2.0 for letting the
 * light steps flip under `.dark`. `green-400` is out of the light ramp at ΔE 4.0
 * against `red-300`. Re-run `validate_palette.js --pairs all` before changing a step.
 */
export const SCORE_BAND_CONFIG = {
  [ScoreBand.CORRECT]: {
    label: "Correct",
    /** Mean score at or above this rounds to the band's rubric level. */
    lowerBound: 3.5,
    cellClass: "bg-green-100 text-green-800 dark:bg-green-100 dark:text-green-700",
    swatchClass: "bg-green-100",
  },
  [ScoreBand.MOSTLY_CORRECT]: {
    label: "Mostly correct",
    lowerBound: 2.5,
    cellClass: "bg-green-300 text-green-900 dark:bg-green-500 dark:text-green-100",
    swatchClass: "bg-green-300 dark:bg-green-500",
  },
  [ScoreBand.MOSTLY_INCORRECT]: {
    label: "Mostly incorrect",
    lowerBound: 1.5,
    cellClass: "bg-red-300 text-white dark:bg-red-500 dark:text-white",
    swatchClass: "bg-red-300 dark:bg-red-500",
  },
  [ScoreBand.INCORRECT]: {
    label: "Incorrect",
    lowerBound: 0,
    cellClass: "bg-red-500 text-white dark:bg-red-800 dark:text-red-100",
    swatchClass: "bg-red-500 dark:bg-red-800",
  },
} as const satisfies Record<
  ScoreBand,
  { label: string; lowerBound: number; cellClass: string; swatchClass: string }
>;

/** Band order for the legend and for `scoreBandFor`, best score first. */
export const SCORE_BAND_ORDER = [
  ScoreBand.CORRECT,
  ScoreBand.MOSTLY_CORRECT,
  ScoreBand.MOSTLY_INCORRECT,
  ScoreBand.INCORRECT,
] as const;

/**
 * Cells with no score to place on the ramp. Hollow rather than a fifth fill: an
 * absence should not compete with the scale for attention.
 */
export const NO_SCORE_CELL_CLASS = {
  [CaseOutcome.ERRORED]: "text-red-600 ring-1 ring-inset ring-red-500",
  [CaseOutcome.NOT_RUN]: "bg-muted/50 ring-1 ring-inset ring-border",
} as const;

/**
 * Where run-to-run pass-rate movement starts being worth attention, and where the
 * case stops being worth trusting. A share of the pass rate: 0.15 is "swings
 * about 15 points per run".
 */
export const FLAKINESS_FLAKY = 0.15;

export const FLAKINESS_VERY_FLAKY = 0.4;
